import isEmpty from 'lodash/isEmpty';
import { genericInvalidMessage } from '@/_constants/form-messages';
import type { IMotoboy } from '@/Interfaces/Data';
import { objectNotNullV2, validateCpf } from '@/Utils/FnUtils';
import type { BaseSchema } from '@/_config/lib/yup';
import { object, string } from '@/_config/lib/yup';
import { validateCnpj } from '@/_utils/text';

export const MOTOBOY_FORM_SCHEMA = object({
  nome: string().required(),
  senha: string()
    .when('$motoboy', (motoboy: IMotoboy | null, schema: BaseSchema) => {
      return motoboy ? schema.optional() : schema.required();
    })
    .test(
      'validate-password',
      'Senha deve conter ao menos 8 caracteres',
      (value?: string) => {
        if (!value) return true;

        return value.length >= 8;
      },
    ),
  email: string().required().email(),
  telefone: string().required().telefone(),
  cpf: string().required().cpf(),
  cnpj: string().cnpj(),
  dataNascimento: string().optional(),
  razaoSocial: string().optional(),
  nomeFantasia: string().optional(),
  cidade: string().required(),

  enderecoResidencia: object({
    cep: string().optional(),
    bairro: string().required(),
    endereco: string().required(),
    numero: string().required(),
    complemento: string().optional(),
    referencia: string().optional(),
  })
    .transform(value => {
      return isEmpty(objectNotNullV2(value)) ? undefined : value;
    })
    .default(undefined),

  documentoVeiculo: object({
    nomeDonoVeiculo: string().required(),
    cpfDonoVeiculo: string().optional().cpf(),
    exercicio: string().optional(),
    renavam: string().required(),
    anoFabricacao: string().optional().ano(),
    anoModelo: string().optional().ano(),
    modelo: string().required(),
    chassi: string().optional(),
    placa: string().required(),
  })
    .transform(value => {
      return isEmpty(objectNotNullV2(value)) ? undefined : value;
    })
    .default(undefined),

  cnh: object({
    numeroCNH: string().required(),
    categoriaCNH: string().required(),
    dataEmissaoCNH: string().required(),
    validadeCNH: string().required(),
  })
    .transform(value => {
      return isEmpty(objectNotNullV2(value)) ? undefined : value;
    })
    .default(undefined),

  tipoVeiculo: string().optional(),
  tipoPessoa: string().required(),

  contaBancaria: object({
    banco: string().required(),
    tipoConta: string().required(),
    bancoAgencia: string().required(),
    bancoAgenciaDigito: string().optional(),
    numeroConta: string().required(),
    digitoConta: string().required(),
    cpfCnpjDestinatario: string()
      .required()
      .test('validate-cpf-cnpj', genericInvalidMessage, value => {
        if (!value) return true;

        const isCpf = value.length === 14;

        if (isCpf) {
          return !validateCpf(value);
        }
        if (!isCpf) {
          return !validateCnpj(value);
        }

        return true;
      }),
    nomeDestinatario: string().required(),
    comprovanteBancarioFile: string().optional(),
  })
    .transform(value => {
      return isEmpty(objectNotNullV2(value)) ? undefined : value;
    })
    .default(undefined),
});

export const CATEGORIAS_CNH = [
  { key: 'ACC', text: 'ACC', value: 'ACC' },
  { key: 'A', text: 'A', value: 'A' },
  { key: 'B', text: 'B', value: 'B' },
  { key: 'C', text: 'C', value: 'C' },
  { key: 'D', text: 'D', value: 'D' },
  { key: 'E', text: 'E', value: 'E' },
  { key: 'AB', text: 'AB', value: 'AB' },
  { key: 'AC', text: 'AC', value: 'AC' },
  { key: 'AD', text: 'AD', value: 'AD' },
  { key: 'AE', text: 'AE', value: 'AE' },
];

export enum VEHICLE_TYPES {
  MOTO = 'MOTO',
  CARRO = 'CARRO',
  BICICLETA = 'BICICLETA',
}

export const VEHICLE_LABELS: Record<VEHICLE_TYPES, string> = {
  [VEHICLE_TYPES.MOTO]: 'Moto',
  [VEHICLE_TYPES.CARRO]: 'Carro',
  [VEHICLE_TYPES.BICICLETA]: 'Bicicleta',
};

export const VEHICLE_TYPES_OPTIONS = [
  { key: VEHICLE_TYPES.MOTO, text: 'Moto', value: VEHICLE_TYPES.MOTO },
  { key: VEHICLE_TYPES.CARRO, text: 'Carro', value: VEHICLE_TYPES.CARRO },
  {
    key: VEHICLE_TYPES.BICICLETA,
    text: 'Bicicleta',
    value: VEHICLE_TYPES.BICICLETA,
  },
];

enum ACCOUNTS_TYPES {
  CURRENT_ACCOUNT = 'CURRENT_ACCOUNT',
  SAVINGS_ACCOUNT = 'SAVINGS_ACCOUNT',
}

export const ACCOUNTS_TYPES_OPTIONS = [
  {
    key: ACCOUNTS_TYPES.CURRENT_ACCOUNT,
    text: 'Corrente',
    value: ACCOUNTS_TYPES.CURRENT_ACCOUNT,
  },
  {
    key: ACCOUNTS_TYPES.SAVINGS_ACCOUNT,
    text: 'Poupança',
    value: ACCOUNTS_TYPES.SAVINGS_ACCOUNT,
  },
];
